import type Phaser from 'phaser';
import { ANIMATION } from '@/config/balance.config';

/**
 * Animator destructibla.
 *
 * Robi:
 *  - delikatny idle pulse (lekkie pulsowanie skali — dyskretne)
 *  - hit reaction (krótki shake gdy obrażenia)
 *  - death animation (scale → 0 + spin, tween Back.easeIn)
 *
 * Każda instancja ma losową fazę pulse'a, żeby 13 obiektów nie pulsowało
 * synchronicznie.
 */
export class DestructibleAnimator {
  private readonly phaseOffset: number = Math.random() * Math.PI * 2;
  private readonly baseX: number;
  private readonly baseY: number;
  private readonly baseScaleX: number;
  private readonly baseScaleY: number;

  // Hit shake state
  private shakeUntil = 0;
  private shakeStartedAt = 0;

  // Hit flash state — biały błysk gdy obiekt przyjmuje obrażenia
  private flashUntil = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sprite: Phaser.GameObjects.Image,
  ) {
    this.baseX = sprite.x;
    this.baseY = sprite.y;
    this.baseScaleX = sprite.scaleX;
    this.baseScaleY = sprite.scaleY;
  }

  update(time: number): void {
    const cfg = ANIMATION.destructible;
    const t = time / 1000 + (cfg.idlePulsePhaseSpread ? this.phaseOffset : 0);

    // Idle pulse — skala oscyluje wokół base
    const pulse = 1 + Math.sin(t * Math.PI * 2 * cfg.idlePulseFrequencyHz) * cfg.idlePulseAmplitude;
    this.sprite.scaleX = this.baseScaleX * pulse;
    this.sprite.scaleY = this.baseScaleY * pulse;

    // Hit shake — w trakcie shake'u podmieniamy x/y na losowy offset
    if (time < this.shakeUntil) {
      const elapsed = time - this.shakeStartedAt;
      const remain = (this.shakeUntil - this.shakeStartedAt);
      const decay = 1 - elapsed / remain;        // siła maleje liniowo do 0
      const amp = cfg.hitShakePx * decay;
      this.sprite.x = this.baseX + (Math.random() - 0.5) * 2 * amp;
      this.sprite.y = this.baseY + (Math.random() - 0.5) * 2 * amp;
    } else {
      this.sprite.x = this.baseX;
      this.sprite.y = this.baseY;
    }

    // Hit flash — krótki biały błysk gdy obiekt przyjmuje obrażenia
    if (time < this.flashUntil) {
      this.sprite.setTintFill(0xffffff);
    } else if (this.sprite.isTinted) {
      this.sprite.clearTint();
    }
  }

  /** Wywoływane gdy destructible przyjmuje obrażenia (ale jeszcze żyje).
   *  Robi: hit shake (3px decay) + biały flash 60ms. To celuje feedback "to się
   *  trafiło" — nie pet (który atakuje), tylko CEL miga, jak w klasycznych grach. */
  triggerHit(): void {
    const cfg = ANIMATION.destructible;
    const now = this.scene.time.now;
    this.shakeStartedAt = now;
    this.shakeUntil = now + cfg.hitShakeMs;
    this.flashUntil = now + ANIMATION.pet.attackHitFlashMs;
  }

  /**
   * Death animation — scale do 0 + spin. Zwraca Promise rozwiązywany gdy
   * animacja się kończy (żeby WorldScene mogło ukryć sprite'a po fakcie).
   */
  playDeathAnimation(): Promise<void> {
    const cfg = ANIMATION.destructible;
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.sprite,
        scaleX: cfg.deathScaleEnd,
        scaleY: cfg.deathScaleEnd,
        rotation: this.sprite.rotation + cfg.deathRotationEnd,
        duration: cfg.deathDurationMs,
        ease: cfg.deathEase,
        onComplete: () => resolve(),
      });
    });
  }

  /** Reset po respawnie (pełna skala, brak rotacji). */
  resetAfterRespawn(): void {
    this.sprite.scaleX = this.baseScaleX;
    this.sprite.scaleY = this.baseScaleY;
    this.sprite.rotation = 0;
    this.sprite.x = this.baseX;
    this.sprite.y = this.baseY;
  }
}
